/**
 * `?autoplay` dev hook: start a fixed all-bot match straight from the page load,
 * fast-forwarded, so a headless browser can play the real game (renderer, clock,
 * turn flow, director) end to end — scripts/browserSmoke.ts drives it, with
 * `?trace` collecting the turn checksums. All-bot matches are deterministic from
 * their options, so the trace is comparable across builds.
 *
 *   ?autoplay&trace&flow=allPlan&mode=dominion&seed=smoke-1&turns=8
 */
import { START_UNWRAPPED } from '../constants/action';
import './urlPreset';   // the Lab's ?preset= expands before this module reads the URL
import type { MenuOptions, GameMode, TurnFlow } from '../types';
import type { MatchSetup } from '../match/setup';
import { smokeSetup } from './smokeSetup';
import type { DuelSetup } from '../ai/actionDuel';
import type { SkirmishSetup } from '../ai/actionSkirmish';
import type { CommanderStyle } from '../ai/squadOrders';
import type { GameState } from '../types';
import { PHYS_DT } from '../constants';
import { getMenuOptions } from '../ui/menu';
import { labFromParams, labLaunch } from '../ui/labConfig';

const params = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
export const AUTOPLAY = !!params?.has('autoplay');
/**
 * `?lean` (the browser smoke): simulate every frame, draw only every LEAN_RENDER_EVERY-th — the smoke
 * compares the simulation's checksums, not pixels, and Chrome draws WebGL on the CPU (SwiftShader),
 * so drawing is most of its cost. A screenshot asks for a frame first (`__orbituaryRenderNow`).
 */
export const LEAN_RENDER_EVERY = params?.has('lean') ? 30 : 1;
/** Fast-forward floor while autoplaying (the director's cap). */
export const AUTOPLAY_TIME_SCALE = AUTOPLAY ? 32 : 1;

type AutoplayStatus = { started: boolean; done: boolean; winner?: string | null; turns?: number; reason?: string | null; resolved?: { trace: string[]; winner: string | null };
  /** ?action&autoplay: the page's final digest and ticks, and auto-resolve's in this page. */
  action?: { checksum: string; ticks: number; winnerTeam: number | null }; actionResolved?: { checksum: string; ticks: number; winnerTeam: number | null } };
const status: AutoplayStatus = { started: false, done: false };
if (AUTOPLAY) (globalThis as unknown as { __orbituaryAutoplay: AutoplayStatus }).__orbituaryAutoplay = status;

/** The match to start, or null when not autoplaying. */
export function autoplayOptions(): MenuOptions | null {
  if (!AUTOPLAY || !params) return null;
  const teams = 2, units = 2;
  status.started = true;
  return {
    ...getMenuOptions(),
    seed: params.get('seed') ?? 'smoke-1',
    numPlanets: Number(params.get('planets') ?? 4),
    numStars: 0, numWormholePairs: 0,
    numTeams: teams, humanTeams: 0, botTeams: teams,
    unitsPerTeam: units, botUnitsPerTeam: units,
    numPlayers: teams * units, numBots: teams * units,
    playerLoadouts: [], unitSlots: [],
    botDifficulty: params.get('difficulty') ?? 'medium',
    botOverrides: [],
    gameMode: (params.get('mode') ?? 'deathmatch') as GameMode,
    modifiers: [],
    turnFlow: (params.get('flow') ?? 'perUnit') as TurnFlow,
    turnLimit: Number(params.get('turns') ?? 8),
    visibility: 'free',
    resolutionPace: 'fastForward',
    systemPreset: 'custom',
    hazardDensity: 'some',
  };
}

/**
 * `?autoplay&setup`: the match a MatchSetup describes, started with Game.startFromSetup —
 * the same setup scripts/browserSmoke.ts auto-resolves with runBattle to compare traces.
 */
export function autoplaySetup(): MatchSetup | null {
  if (!AUTOPLAY || !params || !params.has('setup')) return null;
  status.started = true;
  const setup = smokeSetup({
    seed: params.get('seed') ?? 'smoke-1',
    mode: (params.get('mode') ?? 'deathmatch') as GameMode,
    flow: (params.get('flow') ?? 'perUnit') as TurnFlow,
    turns: Number(params.get('turns') ?? 8),
    ...(params.get('clock') === 'action' ? { clock: 'action' as const } : {}),
    ...(params.has('seats') ? { seats: true } : {}),
  });
  // `&emplacements=N`: N empty emplacements per team (S6), to look at them; `&guns=hand` puts them a short walk from
  // each team's first unit (the seat smoke), not behind the lines (decision 126). 69.13 (d): the gun pit is retired from
  // play — read only beside `&legacy=1`
  const emplacements = params.get('legacy') === '1' ? Number(params.get('emplacements') ?? 0) : 0;
  return emplacements > 0 ? { ...setup, emplacementsPerTeam: emplacements, ...(params.get('guns') === 'hand' ? { gunsAt: 'hand' as const } : {}) } : setup;
}

/**
 * `?autoplay&setup`: auto-resolve the setup in this page (runBattle, on this engine and
 * kernel) before the game watches it, so the smoke can compare the two turn by turn.
 */
export async function resolveForComparison(setup: MatchSetup): Promise<void> {
  const { initWasm } = await import('../ai/wasmPhysics');
  const { runBattle } = await import('../match/runBattle');
  await initWasm();
  const b = await runBattle({ ...setup, traceTurns: true });
  status.resolved = { trace: b.report.turnTrace ?? [], winner: b.report.winnerTeam === null ? null : `team ${b.report.winnerTeam}` };
  if (setup.clock === 'action') {
    const { duelDigest } = await import('../ai/actionDuel');
    status.actionResolved = { checksum: duelDigest(b.state), ticks: -1, winnerTeam: b.report.winnerTeam };
  }
}

export function markAutoplayDone(winner: string | null, turns: number, reason: string | null): void {
  if (!AUTOPLAY) return;
  Object.assign(status, { done: true, winner, turns, reason });
}

/** Autoplay only: expose the game to the smoke script's stall diagnostics. */
export function exposeForAutoplay(game: object): void {
  if (AUTOPLAY || params?.has('smoke')) (globalThis as unknown as { __orbituaryGame: object }).__orbituaryGame = game;
}

/**
 * Phase 59.3 `?action`: a real-time duel over the shoulder. You play team 0 against bots;
 * with `&autoplay` it is all bots, fast-forwarded, and the page also auto-resolves the same
 * duel (runDuel) so the smoke can check watch = auto-resolve by final digest.
 *   ?action&units=2&difficulty=easy&seed=duel-1
 */
/**
 * The URL's person-tuning dials as a setup's `personTuning` (the Lab writes them: ui/labConfig.ts). 69.14's fix-forward: `relief`
 * only when it is given — `Number(null)` is 0, so bullet gravity or speed on the URL without it turned the fine ground off (the
 * owner's URL: no fine ground, no structure seated, the motor pool's yard left empty).
 */
export function urlPersonTuning(params: URLSearchParams): Partial<MatchSetup> {
  const num = (k: string) => { const v = params.get(k); return v === null || v === '' ? NaN : Number(v); };
  if (!params.get('bulletgravity') && !params.get('bulletspeed') && !params.get('scramble') && !params.get('relief')) return {};
  return { personTuning: { ...(num('relief') >= 0 ? { fineRelief: num('relief') } : {}), ...(num('bulletgravity') > 0 ? { bulletGravity: num('bulletgravity') } : {}), ...(num('bulletspeed') > 0 ? { bulletSpeed: num('bulletspeed') } : {}), ...(params.get('scramble') ? { scramble: params.get('scramble') === 'off' ? 0 : Number(params.get('scramble')) || 0 } : {}) } };
}

/** 69.19: the URL's Unwrapped at start (the Lab's row: auto, all, some, none; `unwrappedenemy` the enemy's own) as the setup's per-side shares. */
export function urlStartUnwrapped(params: URLSearchParams): Partial<MatchSetup> {
  const one = (v: string | null): number | 'auto' => v === 'all' || v === 'some' || v === 'none' ? START_UNWRAPPED[v] : 'auto';
  const mine = one(params.get('unwrapped'));
  return { startUnwrapped: [mine, params.get('unwrappedenemy') ? one(params.get('unwrappedenemy')) : mine] };
}

export type ActionLaunch = { setup: DuelSetup; humanTeam: number | null; timeScale: number; tickLimit: number; range: boolean; skirmish: boolean };

export function actionLaunch(): ActionLaunch | null {
  return params ? actionLaunchFrom(params, AUTOPLAY) : null;
}

/**
 * A `?action&…` query's launch: the Lab's own reading — every row the URL carries, a row it leaves out at the Lab's default,
 * built as the Lab's Play builds it (labFromParams → labLaunch), so a pasted Lab URL plays what the panel would (the Copy as
 * URL writes only what differs from the defaults). Over it, what is not a Lab row: a smoke case's tickets, points, scripted
 * withdrawal and reserves, the commanders and difficulty as lists, the older per-side tier spellings, the watched clock's
 * speed. `autoplay` (the smoke's watched page): the page's older defaults where the URL leaves them out.
 */
export function actionLaunchFrom(params: URLSearchParams, autoplay = false): ActionLaunch | null {
  if (!params.has('action')) return null;
  const q = new URLSearchParams(params);
  const skirmish = q.has('skirmish'), range = q.has('range');
  if (autoplay && skirmish && !q.has('units')) q.set('units', '3');
  if (autoplay && !skirmish && !q.has('difficulty')) q.set('difficulty', 'hard');
  if (autoplay && !skirmish && !q.has('time')) q.set('time', '60');
  // (a list: each side its own — the Lab's pair of parameters)
  const diff = (q.get('difficulty') ?? '').split(',');
  if (diff.length > 1 && !q.has('difficultyenemy')) q.set('difficultyenemy', diff[1]);
  // (a pair the Lab writes as one value twice reads as its row; two sides apart, as a list over it)
  const list = (k: string): string[] | null => { const v = q.get(k)?.split(','); return v && v.length > 1 && v.some(x => x !== v[0]) ? v : null; };
  const commanders = list('commanders'), reserves = list('reserves');
  const extra: Partial<SkirmishSetup> = skirmish ? {
    ...(q.get('tickets') ? { tickets: Number(q.get('tickets')) } : {}),
    ...(q.get('points') ? { pointCount: Number(q.get('points')) } : {}),
    ...(commanders ? { commanders: commanders as CommanderStyle[] } : {}),
    // X22: exits on and a scripted withdrawal (team,seconds) for the smoke
    ...(q.get('withdraw') ? { withdraw: { team: Number(q.get('withdraw')!.split(',')[0]), atSeconds: Number(q.get('withdraw')!.split(',')[1] ?? 20) } } : {}),
    ...(reserves ? { reserves: reserves.map(Number) } : {}),   // X19: each team's reserve
    // (62.60's older per-side spellings of the tiers)
    ...(q.get('quietsides') === 'mine' ? { quietTier: [true, false] } : q.get('quietsides') === 'enemy' ? { quietTier: [false, true] } : {}),
    ...(q.get('marchsides') === 'mine' ? { marchTier: [true, false] } : q.get('marchsides') === 'enemy' ? { marchTier: [false, true] } : {}),
    ...(q.get('steadysides') === 'mine' ? { steadyTier: [true, false] } : q.get('steadysides') === 'enemy' ? { steadyTier: [false, true] } : {}),
    ...(q.get('brainratesides') === 'mine' ? { brainRate: [true, false] } : q.get('brainratesides') === 'enemy' ? { brainRate: [false, true] } : {}),
  } : {};
  const c = labFromParams(q);
  // (the time as given where the Lab's row can't hold it — its floor is 30 s, the smoke plays 20; else the row's, an open operation's 30 minutes with it)
  const given = q.has('time') ? Number(q.get('time')) : NaN;
  const l = labLaunch(c, extra, Number.isFinite(given) && given > 0 && given !== c.time ? given : undefined);
  const setup = l.setup as DuelSetup;
  if (autoplay) status.started = true;
  // `speed`: the watched clock's rate (dev; 62.20 profiles at 1×) — wall time per tick only, never which ticks run
  const timeScale = Number(q.get('speed') ?? (autoplay ? 4 : 1));
  return { setup, humanTeam: autoplay ? null : 0, timeScale: skirmish ? timeScale : autoplay ? 4 : 1, tickLimit: l.tickLimit, range, skirmish };
}

/** ?action&skirmish&autoplay: auto-resolve the skirmish in this page first (same engine). */
export async function resolveSkirmishForComparison(setup: DuelSetup): Promise<void> {
  const { runSkirmish } = await import('../ai/actionSkirmish');
  const time = setup.actionTimeLimit ?? 300;
  // ?trace: the per-tick checksums and the input log as [trace] lines, so the smoke can name the first tick and the first input that differ from Node
  const trace = !!params?.has('trace');
  const logs: unknown[][] = [];
  const r = runSkirmish({ ...setup, timeLimit: time, ...(trace ? { traceTicks: 1, onMatch: (m: { inputLog: unknown[] }) => { logs.push(m.inputLog); } } : {}) });
  if (trace) {
    (r.tickTrace ?? []).forEach((c: number, i: number) => console.log(`[trace] t${i} ${c}`));
    (logs[0] ?? []).forEach((e: unknown, i: number) => console.log(`[trace] in${i} ${JSON.stringify(e)}`));
  }
  status.actionResolved = { checksum: r.stateChecksum, ticks: Math.round(r.time / PHYS_DT), winnerTeam: r.winnerTeam };
}

/** ?action&autoplay: auto-resolve the duel in this page first (same engine). */
export async function resolveDuelForComparison(setup: DuelSetup): Promise<void> {
  const { runDuel } = await import('../ai/actionDuel');
  const r = runDuel(setup);
  status.actionResolved = { checksum: r.checksum, ticks: Math.round(r.time / PHYS_DT), winnerTeam: r.winnerTeam };
}

export async function markActionDone(state: GameState, ticks: number, winnerTeam: number | null): Promise<void> {
  if (!AUTOPLAY) return;
  const { duelDigest } = await import('../ai/actionDuel');
  status.action = { checksum: duelDigest(state), ticks, winnerTeam };
  Object.assign(status, { done: true, winner: winnerTeam === null ? null : `team ${winnerTeam}`, turns: 0, reason: 'action' });
}
